export const resizeImage = (base64Str: string, maxDim: number = 512, quality: number = 0.7): Promise<string> => {
  return new Promise((resolve) => {
    if (!base64Str || typeof base64Str !== 'string') {
      return resolve(base64Str || '');
    }
    
    let resolved = false;
    const safeResolve = (val: string) => {
      if (!resolved) {
        resolved = true;
        resolve(val);
      }
    };

    // Safety timeout to guarantee the promise never hangs on background webviews
    const timer = setTimeout(() => {
      safeResolve(base64Str);
    }, 2500);

    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        clearTimeout(timer);
        try {
          let width = img.width || 1;
          let height = img.height || 1;

          // Fast path: if image is already within bounds and compressed (<90KB), return immediately
          if (width <= maxDim && height <= maxDim && base64Str.length < 90000 && base64Str.startsWith('data:image/jpeg')) {
            return safeResolve(base64Str);
          }

          const canvas = document.createElement('canvas');

          if (width > maxDim || height > maxDim) {
            if (width > height) {
              height = (maxDim / width) * height;
              width = maxDim;
            } else {
              width = (maxDim / height) * width;
              height = maxDim;
            }
          }

          canvas.width = Math.max(1, Math.round(width));
          canvas.height = Math.max(1, Math.round(height));
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            return safeResolve(base64Str);
          }

          // Optional visual enhancement
          try {
            ctx.filter = 'contrast(1.08) saturate(1.08) brightness(1.03)';
          } catch (filterErr) {
            // Ignored if ctx.filter is unsupported
          }
          
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Optimize image encoding with configurable quality
          const resized = canvas.toDataURL('image/jpeg', quality);
          safeResolve(resized && resized.length > 50 ? resized : base64Str);
        } catch (canvasErr) {
          console.warn("Canvas export error in resizeImage, falling back to original base64:", canvasErr);
          safeResolve(base64Str);
        }
      };
      img.onerror = (err) => {
        clearTimeout(timer);
        console.warn("Image load error in resizeImage, using original:", err);
        safeResolve(base64Str);
      };
      img.src = base64Str;
    } catch (e) {
      clearTimeout(timer);
      console.warn("Exception in resizeImage:", e);
      safeResolve(base64Str);
    }
  });
};
